import {
  REGION_TAG_COUNTRIES,
  fmtDayTime,
  regionLabel,
  relativeDate,
  timeIn,
  utcOffsetLabel,
  type CuratorStatusTarget,
} from '@remote-medium/shared';
import {
  ErrorState,
  LoadingState,
  buttonVariants,
  cn,
  errorMessage,
  unwrap,
  useApi,
} from '@remote-medium/ui';
import { Clock, FileText, Globe } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';

import { admin, API_ORIGIN } from '../../lib/client.ts';
import { CuratorNotes } from '../../lib/curator-notes.tsx';
import { CandidateStatusChip } from '../../lib/status-chip.tsx';
import { Notice, Page, PageHeader } from '../../lib/ui.tsx';
import { Activity } from './components/Activity.tsx';
import { CandidateMatches } from './components/CandidateMatches.tsx';
import { loadCandidateContext } from './components/context.ts';
import { ProfileCard } from './components/ProfileCard.tsx';
import { StatusCard } from './components/StatusCard.tsx';

/** "APAC" for Sri Lanka; null when the country is in no region tag. */
function regionTag(country: string) {
  for (const [tag, members] of Object.entries(REGION_TAG_COUNTRIES))
    if ((members as readonly string[]).includes(country)) return tag;
  return null;
}

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

export function CandidateDetailPage() {
  const { id } = useParams();
  const { data, error, loading, setData } = useApi(
    () => unwrap(admin.candidates[':id'].$get({ param: { id: id! } })),
    [id],
  );
  const context = useApi(() => loadCandidateContext(id!), [id]);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  if (loading) return <LoadingState />;
  if (error || !data) return <ErrorState error={error} />;
  const c = data;

  async function run(name: string, fn: () => Promise<typeof data>) {
    setBusy(name);
    setActionError(null);
    try {
      setData(await fn());
      return true;
    } catch (e) {
      setActionError(errorMessage(e));
      return false;
    } finally {
      setBusy(null);
    }
  }

  const addNote = (body: string) =>
    run('notes', () =>
      unwrap(admin.candidates[':id'].notes.$post({ param: { id: c.id }, json: { body } })),
    );
  const setStatus = (status: CuratorStatusTarget) =>
    void run('status', () =>
      unwrap(admin.candidates[':id'].status.$post({ param: { id: c.id }, json: { status } })),
    ).then((ok) => ok && context.reload());

  const tag = regionTag(c.country);
  const now = timeIn(c.timezone);
  const matches = context.data?.matches ?? [];

  return (
    <Page className="gap-6">
      <PageHeader
        back={{ to: '/candidates', label: 'Candidates' }}
        title={c.fullName}
        status={<CandidateStatusChip status={c.status} />}
        description={
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <a href={`mailto:${c.email}`} className="text-foreground underline underline-offset-4">
              {c.email}
            </a>
            <span className="inline-flex items-center gap-1.5">
              <Globe aria-hidden="true" className="size-4" />
              {regionLabel(c.country)}
              {tag && ` · ${tag}`}
            </span>
            <span className="inline-flex items-center gap-1.5" title={c.timezone}>
              <Clock aria-hidden="true" className="size-4" />
              {utcOffsetLabel(c.timezone)}
              {now && `, ${now} now`}
            </span>
            <span>
              Joined {relativeDate(c.createdAt)}
              {c.screenedAt &&
                ` · Screened by ${c.screenedByName ?? 'a curator'} ${lowerFirst(fmtDayTime(c.screenedAt))}`}
            </span>
          </span>
        }
        actions={
          c.hasCv && (
            <a
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'no-underline')}
              href={`${API_ORIGIN}/admin/candidates/${c.id}/cv`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FileText data-icon="inline-start" />
              Open CV
            </a>
          )
        }
      />

      {actionError && <Notice tone="error">{actionError}</Notice>}

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
        <div className="flex flex-col gap-6">
          <CandidateMatches
            matches={matches}
            loading={context.loading}
            error={context.error}
            candidateStatus={c.status}
          />
          <ProfileCard c={c} openRoles={context.data?.openRoles ?? []} />
        </div>

        <div className="flex flex-col gap-6">
          <CuratorNotes
            notes={c.notes}
            hiddenFrom="companies or the candidate"
            placeholder="What stood out, what to check, how they came across."
            busy={busy === 'notes'}
            onAdd={addNote}
            onDelete={async (noteId) =>
              setData(
                await unwrap(
                  admin.candidates[':id'].notes[':noteId'].$delete({
                    param: { id: c.id, noteId },
                  }),
                ),
              )
            }
          />
          <StatusCard
            status={c.status}
            // Unknown until the matches load: don't block "Placed" meanwhile.
            introduced={!context.data || matches.some((m) => m.introSentAt)}
            busy={busy === 'status'}
            onChange={setStatus}
          />
          <Activity c={c} ctx={context.data} />
        </div>
      </div>
    </Page>
  );
}
